import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { DollarSign, Loader2, TrendingUp, Lightbulb, RefreshCw, Lock, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

const Band = ({ b }) => {
    const range = b.high - b.low || 1;
    const midPct = ((b.mid - b.low) / range) * 100;
    return (
        <div className="rounded-xl border border-border bg-card p-4">
            <div className="flex items-baseline justify-between">
                <div className="font-heading font-semibold text-foreground">{b.role}</div>
                <div className="text-[10px] text-muted-foreground uppercase tracking-wider">USD annual</div>
            </div>
            <div className="mt-2 flex items-baseline gap-3">
                <span className="mono-score text-xs text-muted-foreground">${b.low?.toLocaleString?.() || b.low}</span>
                <div className="flex-1 h-2 rounded-full bg-muted relative">
                    <div className="absolute h-full bg-gradient-to-r from-emerald-500 to-sky-500 rounded-full" style={{ width: "100%" }} />
                    <div className="absolute -top-1 h-4 w-[3px] bg-foreground rounded-full" style={{ left: `${midPct}%` }} title={`Mid: $${b.mid}`} />
                </div>
                <span className="mono-score text-xs text-muted-foreground">${b.high?.toLocaleString?.() || b.high}</span>
            </div>
            <div className="mt-2 text-[11px] text-muted-foreground">
                <span className="font-semibold text-foreground">Mid ${b.mid?.toLocaleString?.() || b.mid}</span>
                {b.note && <> · {b.note}</>}
            </div>
        </div>
    );
};

export default function SalaryInsights() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [locked, setLocked] = useState(false);

    const load = async (silent = false) => {
        try {
            silent ? setRefreshing(true) : setLoading(true);
            const { data } = await api.get("/professional/salary-insights");
            setData(data);
            setLocked(false);
        } catch (err) {
            // Distinguish "Pro-only" (402) from generic failures so the page
            // renders a friendly upsell instead of "Unable to load".
            if (err?.response?.status === 402) {
                setLocked(true);
            } else {
                toast.error(formatApiError(err));
            }
        } finally {
            setLoading(false); setRefreshing(false);
        }
    };
    useEffect(() => { load(); }, []);

    return (
        <div data-testid="salary-insights-page">
            <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
                <div>
                    <p className="overline">AI Career Assistant</p>
                    <h1 data-testid="dash-page-title" className="mt-1 font-heading font-medium text-2xl sm:text-3xl tracking-tight text-foreground">
                        Salary Insights
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">Benchmark your target salary against the Simonara talent pool and current US market.</p>
                </div>
                <button onClick={() => load(true)} disabled={refreshing} className="btn-outline text-sm inline-flex items-center gap-1 disabled:opacity-60" data-testid="si-refresh">
                    <RefreshCw size={13} className={refreshing ? "animate-spin" : ""} /> Refresh
                </button>
            </div>

            {loading ? (
                <div className="py-16 flex justify-center"><Loader2 size={18} className="animate-spin text-emerald-500" /></div>
            ) : locked ? (
                <div data-testid="salary-locked" className="card-soft p-10 text-center max-w-lg mx-auto">
                    <div className="mx-auto h-14 w-14 rounded-2xl bg-gradient-to-br from-violet-500 to-emerald-500 text-white flex items-center justify-center mb-4">
                        <Lock size={22} />
                    </div>
                    <h2 className="font-heading font-semibold text-xl text-foreground">Salary Insights is a Talent Pro feature</h2>
                    <p className="mt-2 text-sm text-muted-foreground">
                        See how your target compensation compares to peers on Simonara and get personalized negotiation tips. Included with Talent Pro.
                    </p>
                    <Link
                        to="/talent/billing"
                        data-testid="salary-locked-cta"
                        className="btn-primary text-sm inline-flex items-center mt-5"
                    >
                        Upgrade to Talent Pro <ArrowRight size={13} className="ml-1.5" />
                    </Link>
                </div>
            ) : !data ? (
                <div className="card-soft p-12 text-center text-muted-foreground">Unable to load.</div>
            ) : (
                <div className="space-y-6">
                    <motion.div
                        initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                        className="card-soft p-5 border-emerald-500/30 bg-gradient-to-br from-emerald-500/5 to-sky-500/5"
                    >
                        <div className="overline text-emerald-600 inline-flex items-center gap-1"><DollarSign size={13} /> Your benchmark</div>
                        <h2 className="mt-1 font-heading font-semibold text-lg text-foreground">{data.insight.headline}</h2>
                        <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                            <div><div className="text-[10px] text-muted-foreground uppercase tracking-wider">Role</div><div className="text-foreground">{data.candidate.role || "—"}</div></div>
                            <div><div className="text-[10px] text-muted-foreground uppercase tracking-wider">City</div><div className="text-foreground">{data.candidate.city || "—"}</div></div>
                            <div><div className="text-[10px] text-muted-foreground uppercase tracking-wider">Experience</div><div className="text-foreground">{data.candidate.years || "—"}</div></div>
                            <div><div className="text-[10px] text-muted-foreground uppercase tracking-wider">Your target</div><div className="text-foreground">{data.candidate.current_desired_salary || "—"}</div></div>
                        </div>
                    </motion.div>

                    {data.insight.bands?.length > 0 && (
                        <div>
                            <h3 className="font-heading font-semibold text-foreground mb-3 inline-flex items-center gap-2">
                                <TrendingUp size={14} /> Salary bands
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="si-bands">
                                {data.insight.bands.map((b, i) => <Band key={i} b={b} />)}
                            </div>
                        </div>
                    )}

                    {data.insight.tips?.length > 0 && (
                        <div className="card-soft p-5">
                            <h3 className="font-heading font-semibold text-foreground mb-3 inline-flex items-center gap-2">
                                <Lightbulb size={14} className="text-amber-500" /> Negotiation tips
                            </h3>
                            <ul className="space-y-2" data-testid="si-tips">
                                {data.insight.tips.map((t, i) => (
                                    <li key={i} className="flex items-start gap-2 text-sm">
                                        <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-emerald-500 shrink-0" />
                                        <span className="text-foreground">{t}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {(data.peers_same_role.length > 0 || data.peers_same_city.length > 0) && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {data.peers_same_role.length > 0 && (
                                <div className="card-soft p-4">
                                    <div className="overline mb-2">Peers with your role</div>
                                    {data.peers_same_role.slice(0, 6).map((p) => (
                                        <div key={p.value} className="flex items-center justify-between text-xs py-1 border-b border-border/40 last:border-0">
                                            <span className="text-foreground">{p.value}</span>
                                            <span className="mono-score text-muted-foreground">{p.count}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {data.peers_same_city.length > 0 && (
                                <div className="card-soft p-4">
                                    <div className="overline mb-2">Peers in your city</div>
                                    {data.peers_same_city.slice(0, 6).map((p) => (
                                        <div key={p.value} className="flex items-center justify-between text-xs py-1 border-b border-border/40 last:border-0">
                                            <span className="text-foreground">{p.value}</span>
                                            <span className="mono-score text-muted-foreground">{p.count}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
